import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  DollarSign,
  Receipt,
  Building2,
  Settings,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  LogOut,
  Bell,
  Menu,
  X,
  Calculator,
  UserPlus,
  Shield,
  FileSearch,
  CalendarDays,
  Home,
  UserCircle,
  FileText,
  Users2,
  KeyRound,
  ClipboardEdit,
  ClipboardList,
  DatabaseBackup,
  ScrollText,
  TrendingUp,
  Laptop,
  GraduationCap,
  Target,
  BarChart3,
  Mail,
  Clock4,
  MapPin,
  ShieldCheck,
  MonitorSmartphone,
  Workflow,
  Briefcase,
  BookOpen,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';

const adminNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/employees', icon: Users, label: 'Employees' },
  { to: '/onboarding', icon: UserPlus, label: 'Onboarding', roles: ['super_admin', 'hr_admin'] },
  { to: '/attendance', icon: Clock, label: 'Attendance' },
  { to: '/leaves', icon: Calendar, label: 'Leave' },
  { to: '/payroll', icon: DollarSign, label: 'Payroll', roles: ['super_admin', 'hr_admin', 'payroll_admin'] },
  { to: '/expenses', icon: Receipt, label: 'Expenses' },
  { to: '/assets', icon: Laptop, label: 'Assets', roles: ['super_admin', 'hr_admin'] },
  { to: '/training', icon: GraduationCap, label: 'Training', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/performance', icon: Target, label: 'Performance', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/exit-management', icon: LogOut, label: 'Exit Management', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/wfh', icon: Home, label: 'WFH Approvals', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/od', icon: Briefcase, label: 'OD Approvals', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/offboarding', icon: ClipboardList, label: 'Offboarding', roles: ['super_admin', 'hr_admin', 'manager', 'payroll_admin'] },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', roles: ['super_admin', 'hr_admin', 'payroll_admin'] },
  { to: '/leave-analytics', icon: TrendingUp, label: 'Leave Analytics', roles: ['super_admin', 'hr_admin', 'payroll_admin'] },
  { to: '/calendar', icon: CalendarDays, label: 'Calendar' },
  { to: '/reminders', icon: Bell, label: 'Reminders' },
  { to: '/companies', icon: Building2, label: 'Companies' },
];

// Items grouped under the Reports parent (all generated reports + exports)
const reportsSubItems = [
  { to: '/reports/attendance', icon: FileSpreadsheet, label: 'Attendance Report', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/reports/leaves',     icon: FileSpreadsheet, label: 'Leave Report',      roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/reports/expenses',   icon: FileSpreadsheet, label: 'Expense Report',    roles: ['super_admin', 'hr_admin', 'payroll_admin', 'manager'] },
];

// Items grouped under the Settings parent
const settingsSubItems = [
  { to: '/hr-documents/offer-letter', icon: FileText, label: 'Offer Letters', roles: ['super_admin', 'hr_admin'] },
  { to: '/hr-documents/salary-certificate', icon: FileText, label: 'Salary Certificates', roles: ['super_admin', 'hr_admin'] },
  { to: '/hr-documents/templates', icon: FileText, label: 'HR Letters', roles: ['super_admin', 'hr_admin'] },
  { to: '/salary-config', icon: Calculator, label: 'Salary Config', roles: ['super_admin', 'hr_admin', 'payroll_admin'] },
  { to: '/bulk-salary-revision', icon: TrendingUp, label: 'Bulk Salary Revision', roles: ['super_admin', 'hr_admin', 'payroll_admin'] },
  { to: '/attendance-policy', icon: ShieldCheck, label: 'Attendance Policy', roles: ['super_admin', 'hr_admin'] },
  { to: '/attendance-corrections', icon: ClipboardEdit, label: 'Attendance Corrections', roles: ['super_admin', 'hr_admin', 'manager'] },
  { to: '/shifts', icon: Clock4, label: 'Shifts', roles: ['super_admin', 'hr_admin'] },
  { to: '/geofences', icon: MapPin, label: 'Geofences', roles: ['super_admin', 'hr_admin'] },
  { to: '/users', icon: Shield, label: 'User Admin', roles: ['super_admin', 'hr_admin'] },
  { to: '/role-permissions', icon: KeyRound, label: 'Role Matrix', roles: ['super_admin'] },
  { to: '/email-config', icon: Mail, label: 'Email Config', roles: ['super_admin', 'hr_admin'] },
  { to: '/audit-logs', icon: FileSearch, label: 'Audit Logs', roles: ['super_admin', 'hr_admin'] },
  { to: '/leave-policies', icon: ScrollText, label: 'Leave Policies', roles: ['super_admin', 'hr_admin'] },
  { to: '/holidays', icon: CalendarDays, label: 'Holidays', roles: ['super_admin', 'hr_admin'] },
  { to: '/workflows', icon: Workflow, label: 'Approval Workflows', roles: ['super_admin', 'hr_admin'] },
  { to: '/device-trust', icon: Shield, label: 'Device Trust', roles: ['super_admin', 'hr_admin'] },
  { to: '/data-migration', icon: DatabaseBackup, label: 'Data Migration / Go-Live Import', roles: ['super_admin'] },
  { to: '/settings', icon: Settings, label: 'General' },
];

const portalNavItems = [
  { to: '/me', icon: Home, label: 'My Dashboard' },
  { to: '/me/attendance', icon: Clock, label: 'My Attendance' },
  { to: '/me/leave', icon: Calendar, label: 'My Leave' },
  { to: '/me/wfh', icon: Home, label: 'My WFH' },
  { to: '/me/od', icon: Briefcase, label: 'My OD' },
  { to: '/me/payroll', icon: DollarSign, label: 'My Payroll' },
  { to: '/me/expenses', icon: Receipt, label: 'My Expenses' },
  { to: '/me/documents', icon: FileText, label: 'My Documents' },
  { to: '/me/development', icon: GraduationCap, label: 'My Development' },
  { to: '/me/devices', icon: MonitorSmartphone, label: 'My Devices' },
  { to: '/help', icon: BookOpen, label: 'Help' },
  { to: '/me/resignation', icon: LogOut, label: 'My Resignation' },
  { to: '/me/profile', icon: UserCircle, label: 'My Profile' },
];

const managerNavItem = { to: '/my-team', icon: Users2, label: 'My Team', roles: ['manager', 'super_admin', 'hr_admin'] };

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [buildSha, setBuildSha] = useState(null);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Fetch the frontend build commit so admins can confirm the latest build
  // is live after a VPS redeploy. Falls back to env var when build-info.json
  // isn't exposed by the edge (e.g. local dev).
  useEffect(() => {
    fetch('/build-info.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setBuildSha(j?.sha || process.env.REACT_APP_BUILD_SHA || null))
      .catch(() => setBuildSha(process.env.REACT_APP_BUILD_SHA || null));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          bg-card border-r border-border
          flex flex-col
          sidebar-transition
          ${collapsed ? 'w-20' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">PG</span>
              </div>
              <span className="font-bold text-lg">Pride Global</span>
            </div>
          )}
          {collapsed && (
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center mx-auto">
              <span className="text-primary-foreground font-bold text-sm">PG</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex"
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <X size={18} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {(() => {
            const role = user?.role;
            const isEmployee = role === 'employee';
            const isManager = role === 'manager';
            const showAdmin = ['super_admin', 'hr_admin', 'payroll_admin', 'manager'].includes(role);
            const showManager = ['manager', 'super_admin', 'hr_admin'].includes(role);

            // Filter admin items by role
            const adminItems = adminNavItems.filter(item => !item.roles || item.roles.includes(role));

            return (
              <ul className="space-y-1 px-3">
                {showAdmin && (
                  <>
                    {!collapsed && <li className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Admin</li>}
                    {adminItems.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.to === '/'}
                          className={({ isActive }) => `
                            flex items-center gap-3 px-3 py-2.5 rounded-lg
                            transition-colors duration-200
                            ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                            ${collapsed ? 'justify-center' : ''}
                          `}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                          onClick={() => setMobileOpen(false)}
                        >
                          <item.icon size={20} />
                          {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
                        </NavLink>
                      </li>
                    ))}

                    {/* Reports parent with collapsible submenu */}
                    {(() => {
                      const reportsItems = reportsSubItems.filter(item => !item.roles || item.roles.includes(role));
                      if (reportsItems.length === 0) return null;
                      return (
                        <li>
                          <button
                            type="button"
                            onClick={() => setReportsOpen(o => !o)}
                            className={`
                              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                              text-muted-foreground hover:bg-muted hover:text-foreground
                              ${collapsed ? 'justify-center' : ''}
                            `}
                            data-testid="nav-reports-parent"
                          >
                            <FileSpreadsheet size={20} />
                            {!collapsed && <span className="font-medium text-sm flex-1 text-left">Reports</span>}
                            {!collapsed && <ChevronDown size={16} className={`transition-transform ${reportsOpen ? 'rotate-180' : ''}`} />}
                          </button>
                          {reportsOpen && !collapsed && (
                            <ul className="mt-1 ml-3 pl-3 border-l border-border space-y-0.5" data-testid="reports-submenu">
                              {reportsItems.map(sub => (
                                <li key={sub.to}>
                                  <NavLink
                                    to={sub.to}
                                    className={({ isActive }) => `
                                      flex items-center gap-2 px-3 py-2 rounded-md text-sm
                                      transition-colors duration-200
                                      ${isActive ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                                    `}
                                    data-testid={`nav-${sub.label.toLowerCase().replace(/\s/g, '-')}`}
                                    onClick={() => setMobileOpen(false)}
                                  >
                                    <sub.icon size={14} />
                                    {sub.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })()}

                    {/* Settings parent with collapsible submenu */}
                    {(() => {
                      const settingsItems = settingsSubItems.filter(item => !item.roles || item.roles.includes(role));
                      if (settingsItems.length === 0) return null;
                      return (
                        <li>
                          <button
                            type="button"
                            onClick={() => setSettingsOpen(o => !o)}
                            className={`
                              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                              text-muted-foreground hover:bg-muted hover:text-foreground
                              ${collapsed ? 'justify-center' : ''}
                            `}
                            data-testid="nav-settings-parent"
                          >
                            <Settings size={20} />
                            {!collapsed && <span className="font-medium text-sm flex-1 text-left">Settings</span>}
                            {!collapsed && <ChevronDown size={16} className={`transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />}
                          </button>
                          {settingsOpen && !collapsed && (
                            <ul className="mt-1 ml-3 pl-3 border-l border-border space-y-0.5" data-testid="settings-submenu">
                              {settingsItems.map(sub => (
                                <li key={sub.to}>
                                  <NavLink
                                    to={sub.to}
                                    className={({ isActive }) => `
                                      flex items-center gap-2 px-3 py-2 rounded-md text-sm
                                      transition-colors duration-200
                                      ${isActive ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                                    `}
                                    data-testid={`nav-${sub.label.toLowerCase().replace(/\s/g, '-')}`}
                                    onClick={() => setMobileOpen(false)}
                                  >
                                    <sub.icon size={14} />
                                    {sub.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })()}
                  </>
                )}

                {showManager && (
                  <>
                    {!collapsed && <li className="px-3 py-1 mt-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Manager</li>}
                    <li>
                      <NavLink
                        to={managerNavItem.to}
                        className={({ isActive }) => `
                          flex items-center gap-3 px-3 py-2.5 rounded-lg
                          ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                          ${collapsed ? 'justify-center' : ''}
                        `}
                        data-testid={`nav-${managerNavItem.label.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setMobileOpen(false)}
                      >
                        <managerNavItem.icon size={20} />
                        {!collapsed && <span className="font-medium text-sm">{managerNavItem.label}</span>}
                      </NavLink>
                    </li>
                  </>
                )}

                <>
                  {!collapsed && <li className="px-3 py-1 mt-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">{isEmployee ? 'Portal' : 'Self-Service'}</li>}
                  {portalNavItems.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/me'}
                        className={({ isActive }) => `
                          flex items-center gap-3 px-3 py-2.5 rounded-lg
                          ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                          ${collapsed ? 'justify-center' : ''}
                        `}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setMobileOpen(false)}
                      >
                        <item.icon size={20} />
                        {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
                      </NavLink>
                    </li>
                  ))}
                </>
              </ul>
            );
          })()}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className={`
                  flex items-center gap-3 w-full p-2 rounded-lg
                  hover:bg-muted transition-colors
                  ${collapsed ? 'justify-center' : ''}
                `}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.role?.replace('_', ' ')}
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={toggleTheme}>
                {theme === 'dark' ? <Sun size={16} className="mr-2" /> : <Moon size={16} className="mr-2" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Live build badge — if this short SHA doesn't change after a VPS
              update, the frontend container did NOT rebuild. See DEPLOYMENT.md
              § "Verifying what commit is live". */}
          {!collapsed && (
            <div className="mt-3 px-2 flex items-center justify-between text-[10px] text-muted-foreground">
              <span>Build</span>
              <span
                className="font-mono px-1.5 py-0.5 rounded bg-muted"
                data-testid="build-sha-badge"
                title={buildSha ? `Deployed commit: ${buildSha}` : 'Build info not available'}
              >
                {buildSha ? buildSha.slice(0, 7) : 'dev'}
              </span>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 border-b border-border glass sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu size={20} />
            </Button>
            <h1 className="text-lg font-semibold hidden sm:block">
              Human Resource Management System
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell size={20} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
